import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, BookOpen, CalendarClock, ChevronRight, Eye, Facebook, FileText,
  Globe, Image as ImageIcon, Instagram, Loader2, Send, Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import EditionForm from "@/components/EditionForm";
import StoryCardPanel from "@/components/StoryCardPanel";
import CarouselPanel from "@/components/CarouselPanel";
import FacebookSharePanel from "@/components/FacebookSharePanel";
import ScheduledPostsList from "@/components/ScheduledPostsList";
import { CoverPicker, StatusPill, fileSrc } from "@/pages/DigitalEditionsPage";
import { formatInTz, propertyTimezone, tzAbbreviation, utcIsoToLocalInput } from "@/lib/timezone";

/**
 * One digital edition (2026-10-19): edit its fields, set the cover,
 * post it to WordPress (draft / schedule / live — the same publish
 * route as articles), then share it with the same social panels the
 * Article page uses.
 */
export default function EditionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [showEmbed, setShowEmbed] = useState(false);

  const { data: article, isLoading } = useQuery({
    queryKey: ["article", id],
    queryFn: () => api.getArticle(id),
  });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const tz = propertyTimezone(settings);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["article", id] });
    qc.invalidateQueries({ queryKey: ["editions"] });
    qc.invalidateQueries({ queryKey: ["content-calendar"] });
  };

  const saveMut = useMutation({
    mutationFn: (values) => api.updateEdition(id, values),
    onSuccess: () => {
      toast.success(
        article?.wordpress_post_id
          ? "Saved. Click “Update on WordPress” to send the change to the website."
          : "Saved",
      );
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const coverMut = useMutation({
    mutationFn: (file) => api.uploadArticleImage(id, file),
    onSuccess: () => {
      toast.success(
        article?.wordpress_post_id
          ? "Cover replaced. Click “Update on WordPress” to send it to the website."
          : "Cover saved",
      );
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Cover upload failed"),
  });

  const publishMut = useMutation({
    mutationFn: ({ status, scheduledDate }) => api.publishArticle(id, status, scheduledDate),
    onSuccess: (data) => {
      const wasUpdate = !!article?.wordpress_post_id;
      let kind;
      if (data.status === "published_live") kind = wasUpdate ? "Updated live on" : "Published live to";
      else if (data.status === "scheduled") kind = wasUpdate ? "Schedule updated on" : "Scheduled to";
      else kind = wasUpdate ? "Draft updated on" : "Sent as draft to";
      toast.success(`${kind} WordPress`);
      setScheduleOpen(false);
      setScheduleAt("");
      refresh();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "WordPress publish failed"),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.deleteArticle(id),
    onSuccess: () => {
      toast.success("Edition moved to the trash. The WordPress post, if any, is untouched.");
      qc.invalidateQueries({ queryKey: ["editions"] });
      navigate("/editions");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  if (isLoading || !article) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[var(--ink-muted)]" />
      </div>
    );
  }
  if (article.kind !== "edition") {
    return (
      <div className="p-10 text-sm">
        This isn't a digital edition. <Link to={`/article/${id}`} className="underline">Open it as an article</Link>.
      </div>
    );
  }

  const ed = article.edition || {};
  const posted = !!article.wordpress_post_id;
  const hasCover = !!article.generated_image_url;
  const busy = publishMut.isPending;
  const needCover = () => {
    if (!hasCover) {
      toast.error("Upload the cover image first.");
      return true;
    }
    return false;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-[var(--border)] bg-white">
        <div className="px-4 sm:px-10 py-3 flex items-center gap-3 flex-wrap">
          <Link to="/editions" className="text-[var(--ink-muted)] hover:text-[var(--ink)]" title="All editions">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <BookOpen className="w-4 h-4 text-[var(--accent)]" />
          <h1 className="font-serif-display text-xl truncate min-w-0" data-testid="edition-title">{article.headline}</h1>
          <StatusPill status={article.status} />
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button
              type="button"
              data-testid="edition-publish-draft"
              onClick={() => !needCover() && publishMut.mutate({ status: "draft" })}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--slate-900)] text-[var(--slate-900)] hover:bg-[var(--slate-900)] hover:text-white disabled:opacity-60"
            >
              <FileText className="w-3.5 h-3.5" />
              {posted ? "Revert to draft" : "Send as draft"}
            </button>
            <button
              type="button"
              data-testid="edition-publish-schedule"
              onClick={() => setScheduleOpen((o) => !o)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-blue-600 text-blue-700 hover:bg-blue-600 hover:text-white disabled:opacity-60"
            >
              <CalendarClock className="w-3.5 h-3.5" />
              {posted ? "Reschedule" : "Schedule"}
            </button>
            <button
              type="button"
              data-testid="edition-publish-live"
              onClick={() => !needCover() && publishMut.mutate({ status: "publish" })}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {posted ? "Update on WordPress" : "Publish now"}
            </button>
          </div>
        </div>

        {scheduleOpen && (
          <div
            data-testid="edition-schedule-panel"
            className="px-4 sm:px-10 py-3 bg-blue-50 border-t border-blue-200 flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap"
          >
            <div className="flex items-center gap-2 text-sm text-blue-900">
              <CalendarClock className="w-4 h-4 text-blue-700 shrink-0" />
              <span>Schedule the edition to go live on the website:</span>
            </div>
            <input
              data-testid="edition-schedule-input"
              type="datetime-local"
              value={scheduleAt}
              min={utcIsoToLocalInput(new Date(Date.now() + 5 * 60000).toISOString(), tz)}
              onChange={(e) => setScheduleAt(e.target.value)}
              className="border border-[var(--border)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:border-[var(--slate-900)]"
            />
            <span className="text-[11px] font-mono-display text-blue-700">
              {tz} · {tzAbbreviation(tz)}
            </span>
            <button
              type="button"
              data-testid="edition-schedule-confirm"
              onClick={() => {
                if (!scheduleAt) {
                  toast.error("Pick a date and time first");
                  return;
                }
                if (needCover()) return;
                publishMut.mutate({ status: "schedule", scheduledDate: `${scheduleAt}:00` });
              }}
              disabled={busy || !scheduleAt}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />}
              Schedule in WordPress
            </button>
            <button
              type="button"
              onClick={() => {
                setScheduleOpen(false);
                setScheduleAt("");
              }}
              className="text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] sm:ml-2"
            >
              Cancel
            </button>
          </div>
        )}

        {article.wordpress_link && (
          <div
            className={
              "px-4 sm:px-10 py-2 border-t text-xs flex items-center gap-2 flex-wrap " +
              (article.status === "scheduled" ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200")
            }
          >
            {article.status === "scheduled" ? (
              <>
                <CalendarClock className="w-3.5 h-3.5 text-amber-800 shrink-0" />
                <span className="text-amber-900">
                  Scheduled for {formatInTz(article.wordpress_scheduled_date, tz)} ·
                </span>
              </>
            ) : (
              <Globe className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
            )}
            <a
              href={article.wordpress_link}
              target="_blank"
              rel="noreferrer"
              className="underline truncate text-[var(--ink)]"
              data-testid="edition-wp-link"
            >
              {article.wordpress_link}
            </a>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-6 lg:p-10 space-y-6">
          <div className="bg-white border border-[var(--border)] p-5">
            <div className="flex items-start gap-4 flex-wrap">
              {hasCover ? (
                <img
                  src={fileSrc(article.generated_image_url)}
                  alt=""
                  data-testid="edition-cover"
                  className="w-48 max-w-full h-auto border border-[var(--border)]"
                />
              ) : (
                <div className="w-48 h-28 border border-dashed border-[var(--border)] flex items-center justify-center text-[11px] text-[var(--ink-muted)] text-center px-2">
                  No cover yet — needed before posting
                </div>
              )}
              <div className="flex-1 min-w-[220px]">
                <CoverPicker
                  file={null}
                  label={hasCover ? "Replace cover image" : "Cover image"}
                  onChange={(file) => file && coverMut.mutate(file)}
                />
                {coverMut.isPending && (
                  <div className="mt-2 text-[11px] text-[var(--ink-muted)] flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white border border-[var(--border)] p-5">
            <h2 className="font-serif-display text-xl mb-4">Edition</h2>
            <EditionForm
              key={article.id}
              initial={{
                edition_date: ed.edition_date,
                title: article.headline,
                issue_number: ed.issue_number,
                view_url: ed.view_url,
                embed_code: ed.embed_code,
              }}
              settings={settings}
              siteName={settings?.name || ""}
              busy={saveMut.isPending}
              submitLabel="Save changes"
              onSubmit={(v) => saveMut.mutate(v)}
              testId="edition-edit"
            />
            {ed.embed?.src && (
              <div className="mt-4 border-t border-[var(--border)] pt-4">
                <button
                  type="button"
                  onClick={() => setShowEmbed((s) => !s)}
                  className="inline-flex items-center gap-1.5 text-sm text-[var(--ink-muted)] hover:text-[var(--ink)]"
                  data-testid="edition-embed-preview-toggle"
                >
                  <Eye className="w-3.5 h-3.5" /> {showEmbed ? "Hide" : "Preview"} the embedded edition
                </button>
                {showEmbed && (
                  <div className="mt-3 overflow-auto">
                    <iframe
                      src={ed.embed.src}
                      width={ed.embed.width}
                      height={ed.embed.height}
                      title={article.headline}
                      style={{ border: 0, maxWidth: "100%" }}
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="overline mb-2">Share it</div>
            {!article.wordpress_link && (
              <p className="text-[11px] text-[var(--ink-muted)] mb-2">
                Post the edition to the website first, so the social posts can link to it.
              </p>
            )}
            <Section title="Story card · Instagram / Facebook" icon={ImageIcon} testId="edition-section-story-card">
              <StoryCardPanel article={article} settings={settings} />
            </Section>
            <Section title="Instagram carousel" icon={Instagram} testId="edition-section-carousel">
              <CarouselPanel article={article} settings={settings} />
            </Section>
            <Section title="Publish to Facebook" icon={Facebook} testId="edition-section-fb">
              <FacebookSharePanel article={article} />
            </Section>
            <Section title="Scheduled social posts" icon={CalendarClock} testId="edition-section-scheduled">
              <ScheduledPostsList articleId={article.id} />
            </Section>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              data-testid="edition-delete"
              onClick={() => {
                if (window.confirm("Move this edition to the trash? The WordPress post, if any, stays as it is.")) {
                  deleteMut.mutate();
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[var(--ink-muted)] hover:text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" /> Move to trash
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, testId, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testId} className="mb-3">
      <div
        className={
          "w-full flex items-center gap-2 border bg-white transition-colors " +
          (open ? "border-[var(--ink)]" : "border-[var(--border)]")
        }
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 flex items-center gap-2 px-4 py-2.5 hover:bg-neutral-50 min-w-0"
        >
          <ChevronRight
            className={"w-3.5 h-3.5 text-[var(--ink-muted)] transition-transform shrink-0 " + (open ? "rotate-90" : "")}
          />
          <Icon className="w-3.5 h-3.5 text-[var(--accent)] shrink-0" />
          <span className="overline text-left truncate">{title}</span>
        </button>
      </div>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}
